import React, { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Badge,
  Dialog,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
} from "@mui/material";
import { ClipboardList } from "lucide-react";
import { Delete } from "@mui/icons-material";
import SvgPreview from "../SvgPreview";
import { heading } from "../CustomTexts";
import globalColors from "../../globalColors";
import HeaderProfile from "../HeaderProfile";
import { displayRow, rowCenter } from "../../GlobalStyles";
import { allStaticText } from "../../store/slices/constants/staticContents/AllTexts";
import { useAppDispatch, useAppSelecter } from "../../hooks/storeHooks";
import { ObjectValues } from "../../pages/login";
import { updateRefetch } from "../../store/slices/userManagement";
import { getNextPath } from "../../pages/ContentMangements/Utils";
import CustomIconButton from "../CustomIconButton";
import { convertFirstUpper } from "../../Regex";
import useCopiedData from "../../store/slices/constants/staticContents/useCopiedData";

function CustomHeader() {
  const { copiedData, setCopiedData ,refetchCopy} = useCopiedData();
  const [openModal, setOpenModal] = useState(false);
  const dispatch = useAppDispatch();


  // 🔹 Load copied content
  const loadCopiedData = () => {
    try {
      const stored = localStorage.getItem("copied");
      if (stored) {
        const decoded = atob(stored);
        const parsed = JSON.parse(decoded);
        if (Array.isArray(parsed)) setCopiedData(parsed);
      }
    } catch (err) {
      console.warn("Error reading copied data:", err);
    }
  };

  useEffect(() => {
    loadCopiedData();
  }, []);

  useEffect(() => {
    if (refetchCopy) {
      loadCopiedData();
      dispatch(updateRefetch(false));
    }
  }, [refetchCopy]);

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);

  const persistCopiedData = (updated: any[]) => {
    setCopiedData(updated);
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(updated))));
    localStorage.setItem("copied", encoded);
    setTimeout(() => {
      dispatch(updateRefetch(true));
    }, 1);
  };

  // 🔹 Delete an entire parent/entry
  const handleDelete = (idx: number) => {
    const updated = copiedData.filter((_, i) => i !== idx);
    persistCopiedData(updated);
  };

  // 🔹 Delete a single child/question chip
  const handleChildDelete = (parentIdx: number, idToRemove: any) => {
    const updated = copiedData.map((item, idx) => {
      if (idx !== parentIdx) return item;

      // If parent type
      if (item.parent && Array.isArray(item.childs)) {
        return {
          ...item,
          childs: item.childs.filter((child: any) => child.id !== idToRemove),
        };
      }

      // If question-type
      if (Array.isArray(item.questions)) {
        return {
          ...item,
          questions: item.questions.filter((q: any) => q.id !== idToRemove),
        };
      }

      return item;
    });

    persistCopiedData(updated);
  };

  return (
    <>
      {/* 🔹 Header */}
      <Stack
        spacing={"15px"}
        direction="row"
        justifyContent={"space-between"}
        alignItems="center"
        padding={"10px 40px"}
        sx={{
          background: globalColors.primary.pureWhite,
          boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.05)",
        }}
      >
        {/* Left: Logo + App Name */}
        <Box sx={{ ...rowCenter }}>
          <SvgPreview
            sx={{ height: "32px", width: "32px", marginRight: "20px" }}
            svg={allStaticText.LoginContents.logo}
          />
          {heading({
            text: allStaticText.appName,
            size: "1.5rem",
            weight: 400,
          })}
        </Box>

        {/* Middle: Copied Badge */}
        {copiedData.length > 0 && (
          <Box onClick={handleOpen} sx={{ cursor: "pointer", ...rowCenter }}>
            <Badge
              color="primary"
              badgeContent={copiedData.length}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <ClipboardList size={28} color={globalColors.primary.main} />
            </Badge>
            <Typography sx={{ ml: 1, color: globalColors.primary.normalText }}>
              {copiedData.length === 1
                ? copiedData[0].parent
                  ? `${convertFirstUpper(
                      getNextPath(copiedData[0].parent.type).toLowerCase()
                    )}${
                      copiedData[0].childs.length > 1 ? "s are" : " is"
                    } waiting to be pasted`
                  : `Mocktest Questions are waiting to be pasted`
                : "Copied content"}
            </Typography>
          </Box>
        )}

        {/* Right: Profile */}
        <HeaderProfile />
      </Stack>

      {/* 🔹 Dialog */}
      {copiedData.length > 0 && (
        <Dialog open={openModal} onClose={handleClose} maxWidth="md" fullWidth>
          <Box sx={{ p: 3 }}>
            <Box  mb={2}sx={{  ...displayRow }}>
           
              <ClipboardList size={28} color={globalColors.primary.main} />
        
            <Typography variant="h6"ml="10px">
             {copiedData.length === 1
                ? copiedData[0].parent
                  ? `${convertFirstUpper(
                      getNextPath(copiedData[0].parent.type).toLowerCase()
                    )}${
                      copiedData[0].childs.length > 1 ? "s are" : " is"
                    } waiting to be pasted`
                  : `Mocktest Questions are waiting to be pasted`
                : "Copied content"}
            </Typography>
          </Box>

            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9fafc" }}>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>IDs</TableCell>
                    <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {copiedData.map((item, idx) => {
                    const isParent = item.parent;
                    const ids = isParent
                      ? item.childs?.map((q: any) => ({
                          id: q.id,
                          label: q.text,
                        }))
                      : item.questions?.map((q: any) => ({
                          id: q.id,
                          label: q.id,
                        }));

                    return (
                      <TableRow
                        key={idx}
                        hover
                        sx={{
                          "&:hover": { backgroundColor: "#f7f9fc" },
                        }}
                      >
                        <TableCell>{idx + 1}</TableCell>

                        {/* Source */}
                        <TableCell>
                          {isParent ? (
                            <Chip
                              label={`${convertFirstUpper(
                                item.parent.type.toLowerCase()
                              )}: ${item.parent.label}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          ) : (
                            <Typography variant="body2">
                              {item.series?.label} → {item.mocktest?.label} →{" "}
                              {item.section?.label}
                            </Typography>
                          )}
                        </TableCell>

                        {/* Details */}
                        <TableCell>
                          {isParent ? (
                            <Typography variant="body2">
                              {item.childs.length}{" "}
                              {convertFirstUpper(
                                getNextPath(item.parent.type).toLowerCase()
                              )}
                              {item.childs.length > 1 ? "s" : ""}
                            </Typography>
                          ) : (
                            <Typography variant="body2">
                              {item.questions.length} Questions
                            </Typography>
                          )}
                        </TableCell>

                        {/* IDs — deletable chips */}
                        <TableCell>
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {ids?.map(({ id, label }: any) => (
                              <Tooltip key={id} title="Remove this item">
                                <Chip
                                  label={label}
                                  size="small"
                                  variant="outlined"
                                  onDelete={() => handleChildDelete(idx, id)}
                                  deleteIcon={
                                    <Delete sx={{ fontSize: "0.9rem" }} />
                                  }
                                  sx={{
                                    cursor: "pointer",
                                    fontSize: "0.75rem",
                                  }}
                                />
                              </Tooltip>
                            ))}
                          </Stack>
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="center">
                          <Tooltip title="Delete this entry">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(idx)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ textAlign: "right", mt: 3 }}>
              <CustomIconButton content="Close" onClick={handleClose} />
            </Box>
          </Box>
        </Dialog>
      )}
    </>
  );
}

export default CustomHeader;
